import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { BulkProductsRequest } from '@nucleus-commerce/api-client/admin';

import { scheduleKeys } from '@/features/schedules';
import { storeCatalogKeys } from '@/features/store-catalog';

import { productsApi } from '../api/products-api';

import { productKeys } from './product-keys';

/** One-shot dates show in the schedules' Upcoming list, so these actions refresh it. */
const SCHEDULE_ACTIONS = new Set<BulkProductsRequest['action']>(['schedule', 'clear_schedule']);

/**
 * One call for up to 5,000 products, as ids or a selection. A 207 is a resolved promise with
 * failed items; the list page shows them per row, so no toast here. Publishing touches each
 * target store's catalog too, and scheduling the schedules' timeline.
 */
export function useBulkProducts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkProductsRequest) => productsApi.bulk(body),
    meta: { silentError: true },
    onSuccess: (_result, body) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: productKeys.all }),
        ...(body.storeIds ?? []).map((storeId) =>
          queryClient.invalidateQueries({ queryKey: storeCatalogKeys.all(storeId) }),
        ),
        ...(SCHEDULE_ACTIONS.has(body.action)
          ? [queryClient.invalidateQueries({ queryKey: scheduleKeys.all })]
          : []),
      ]),
  });
}
